/* The landing's first screen, matched to the ad that sent the visitor.
 *
 * Every Reddit ad in the November round is a screenshot of one finding from a
 * player's own games ("Your habit there is exd5 — scoring 35%"). The landing
 * answered it with the product's name and an abstract paragraph, and on the
 * round's first day 7 in 10 ad visitors left within 3 seconds: nothing on the
 * page looked like what they had tapped. So the first screen shows the same
 * picture, cropped to the finding, under that ad's own promise, and the rest
 * of the features follow below it for whoever stays.
 *
 * The ad is named by the link's utm_content, which gtm-automation sets per
 * creative, so none of this needs a change to the ads or a new Reddit review.
 * A visitor with no known ad (search, the Play listing, a shared link) gets
 * the composite, which is the whole pitch in one picture.
 *
 * The pictures are public/landing/v<rev>/<picture>-<locale>-<width>.webp, each
 * in two widths so a dense phone screen gets the sharp one and a plain one
 * the light one. The cards and the phone screens are rendered at 4x from a
 * real account (Pablo's progress and his Lichess games, in a local copy of the
 * app, opponents shown by rating only); the composite is its ad's own HTML at
 * 1.5x; the three `_ad` pictures are the ads' own images. Hosting caches
 * pictures for a day (firebase.json), so a re-rendered picture bumps its `rev`
 * and goes in that folder, or returning visitors keep the old one for a day.
 * Sizes are here so the page reserves the space before a picture loads and
 * the button does not jump; adLanding.test.js reads them off the files.
 */

/* Which landing this is, recorded on every visit (landing_visits.v) and on
 * the signup it may become (acquisition.landing), so a change can be judged
 * against what came before it. 1 was the landing until 4 Nov 2026: the name,
 * a paragraph and a sign-in card (records without a version are that one).
 * 2 is this one: the tapped ad's promise and picture, one button, and How it
 * works. Bump it with any change to the first screen worth comparing, and say
 * here what changed. */
export const LANDING_VERSION = 2;

/** The composite: the hero for a visitor no ad explains. */
export const DEFAULT_LANDING_AD = "coach_ledger";

/* Each picture: its revision (the folder), whether it is a phone screen (shown
 * in a phone frame, so the app's buttons in it are not taken for the page's),
 * the widths it comes in, and its height at the widest, per language (Spanish
 * runs longer). Two kinds, by what reads clean on the page:
 *   - a card the app draws on its own (Today, the last games) and the
 *     composite, which carries its own phone: shown as they are;
 *   - anything with more than one surface in it: a whole phone screen, header
 *     and tab bar included, in a frame. */
const PICTURES = {
  today_habit: { rev: 2, widths: [800, 1600], en: 1481, es: 1556 },
  last_games: { rev: 2, widths: [800, 1600], en: 1386, es: 1386 },
  /* `fade`: the crop cuts the composite's phone off at the bottom, so it fades out there. */
  coach_ledger: { rev: 2, fade: true, widths: [800, 1600], en: 1067, es: 1067 },
  ledger_screen: { rev: 4, framed: true, widths: [600, 1200], en: 2665, es: 2665 },
  prep_screen: { rev: 4, framed: true, widths: [600, 1200], en: 2665, es: 2665 },
  course_screen: { rev: 4, framed: true, widths: [600, 1200], en: 2665, es: 2665 },
  /* Three ads are phone screenshots themselves: their first screen is the very
   * picture that was tapped, in the frame. 1080px is all they have. */
  study_sicilian_ad: { rev: 4, framed: true, widths: [600, 1080], en: 844, es: 847 },
  prep_courses_ad: { rev: 4, framed: true, widths: [600, 1080], en: 1350, es: 1350 },
  drill_habit_ad: { rev: 4, framed: true, widths: [600, 1080], en: 1350, es: 1350 },
};

/* Every ad's utm_content, and the picture its first screen shows. */
const HEROES = {
  study_sicilian: "study_sicilian_ad",
  today_habit: "today_habit",
  last_games: "last_games",
  drill_habit: "drill_habit_ad",
  prep_courses: "prep_courses_ad",
  coach_ledger: "coach_ledger",
};
export const LANDING_ADS = Object.keys(HEROES);

/* The feature list under the first screen, in the order the composite tells
 * it: reads your games, finds your leaks, trains the fix. `copy` names its
 * words (app.landingAds.<copy>) and is the ad it stands for, so the ad
 * already on the first screen is not repeated; `step` is which of the three. */
export const LANDING_FEATURES = [
  { copy: "study_sicilian", picture: "ledger_screen", step: "read" },
  { copy: "today_habit", picture: "today_habit", step: "find" },
  { copy: "last_games", picture: "last_games", step: "find" },
  { copy: "prep_courses", picture: "prep_screen", step: "train" },
  { copy: "course_pirc", picture: "course_screen", step: "train" },
];

/** The ad a visitor came from, by its utm_content; the composite otherwise. */
export function landingAd(content) {
  return Object.hasOwn(HEROES, content || "") ? content : DEFAULT_LANDING_AD;
}

/* How wide a picture is drawn, for picking from its srcSet: a card fills the
 * column on a phone (420px at most), a phone frame about three quarters of it
 * (280px at most). A 3x phone then takes the widest file. */
const SIZES = {
  card: "(max-width: 480px) calc(100vw - 40px), 420px",
  framed: "(max-width: 480px) 72vw, 280px",
};

/** A picture in the reader's language (English for anything without its own), at every width it comes in. */
export function landingImage(name, locale) {
  const key = Object.hasOwn(PICTURES, name) ? name : DEFAULT_LANDING_AD;
  const picture = PICTURES[key];
  const lang = locale === "es" ? "es" : "en";
  const widest = picture.widths.at(-1);
  const url = (w) => `/landing/v${picture.rev}/${key}-${lang}-${w}.webp`;
  return {
    src: url(widest),
    srcSet: picture.widths.map((w) => `${url(w)} ${w}w`).join(", "),
    sizes: picture.framed ? SIZES.framed : SIZES.card,
    width: widest,
    height: picture[lang],
    framed: Boolean(picture.framed),
    fade: Boolean(picture.fade),
  };
}

/** The first screen's picture for an ad. */
export function heroImage(ad, locale) {
  return landingImage(HEROES[landingAd(ad)], locale);
}

/** Every picture the page can show, for the test that checks the files. */
export const LANDING_PICTURES = Object.keys(PICTURES);

/* The features below the first screen: every one but the ad already shown. */
export function moreFeatures(ad) {
  return LANDING_FEATURES.filter((feature) => feature.copy !== ad);
}
